import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { IPriceComponent } from '~/utils/interface/estimate';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '~/redux/hooks';
import { priceComponentActions } from '~/thunks/price-components/priceComponentsSlice';
import { handleCreatePriceComponent, handleEditPriceComponent } from '~/thunks/price-components/priceComponentsThunk';

type EditPriceComponentDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: IPriceComponent;
  children?: React.ReactNode;
};

const initialValues: IPriceComponent = {
  name: '',
};

const EditPriceComponentDialog = (props: EditPriceComponentDialogProps) => {
  const { t } = useTranslation(['admin/price-component']);
  const { mode = DIALOG_MODE_CREATE, data } = props;
  const dispatch = useAppDispatch();

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
    form.resetForm();
  };

  const handleCancel = () => {
    handleSetOpen(false);
  };

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        if (mode === DIALOG_MODE_CREATE) {
          await dispatch(handleCreatePriceComponent(values)).unwrap();
          dispatch(priceComponentActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
        } else if (mode === DIALOG_MODE_EDIT && data?.id) {
          await dispatch(handleEditPriceComponent({ id: data.id, params: values })).unwrap();
          dispatch(priceComponentActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
        }
        handleSetOpen(false, true);
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });

  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT && data) {
      form.setFieldValue('name', data.name || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addNewPriceComponent') : t('editPriceComponent')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <Box className='gap-[20px] mb-[20px]'>
            <InputLabel>{t('priceComponentName')}</InputLabel>
            <TextField
              name='name'
              placeholder={t('priceComponentName')}
              value={form.values.name}
              onChange={form.handleChange}
              error={Boolean(form.errors.name)}
              helperText={form.errors.name}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
            if (props.setOpenDrawer) {
              props.setOpenDrawer(false);
            }
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditPriceComponentDialog);
